import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { Card, CardHeader } from '@/components/common/Card';
import { ConfirmDialog } from '@/components/common/Modal';
import { ErrorState, LoadingState } from '@/components/common/States';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import { ROUTES } from '@/app/router/routes';
import { formatDate, humanise, orDash } from '@/utils/format';
import { StudentFormModal, toPayload } from './StudentForm';
import { EnrollStudentModal } from './EnrollStudentModal';
import { AcademicHistoryPanel } from './AcademicHistoryPanel';
import { StudentDocumentsPanel } from './StudentDocumentsPanel';
import { StudentIdentificationsPanel } from './StudentIdentificationsPanel';
import { StudentGuardiansPanel } from '@/features/guardians/StudentGuardiansPanel';
import { StudentLifecycleMenu } from './StudentLifecycleMenu';
import { StudentStatusHistoryPanel } from './StudentStatusHistoryPanel';
import { PortalAccountPanel } from './PortalAccountPanel';

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'guardians', label: 'Guardians' },
  { id: 'history', label: 'Academic history' },
  { id: 'records', label: 'Identification & documents' },
] as const;

type TabId = (typeof TABS)[number]['id'];

/**
 * A single student: personal details, current placement, and full academic history.
 *
 * The history is the point of the screen. Because enrolments are closed rather
 * than overwritten, every year the student has attended remains answerable —
 * which is exactly what a "current grade" column on the student record would
 * have destroyed.
 */
export function StudentDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<TabId>('overview');
  const [editing, setEditing] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const student = useQuery({
    queryKey: queryKeys.students.detail(id),
    queryFn: () => studentsApi.get(id),
    enabled: id !== '',
  });


  const update = useMutation({
    mutationFn: (payload: Parameters<typeof studentsApi.update>[1]) => studentsApi.update(id, payload),
    onSuccess: () => {
      toast.success('Student updated');
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
    },
  });

  const remove = useMutation({
    mutationFn: () => studentsApi.remove(id),
    onSuccess: () => {
      toast.success('Student deleted');
      void queryClient.invalidateQueries({ queryKey: queryKeys.students.all });
      navigate(ROUTES.students, { replace: true });
    },
    onError: (error) => {
      // Refused once the student has enrolment history — deleting would erase it.
      handleApiError(error);
      setDeleting(false);
    },
  });

  if (student.isPending) {
    return <LoadingState label="Loading student" />;
  }
  if (student.error !== null) {
    return <ErrorState error={student.error} onRetry={() => void student.refetch()} />;
  }

  const record = student.data;
  const placement = record.currentPlacement;


  return (
    <>
      <Link to={ROUTES.students} className="mb-3 inline-block text-sm text-muted hover:text-ink">
        ← Back to students
      </Link>

      <PageHeader
        title={record.fullName}
        description={`Student number ${record.studentNumber}`}
        action={
          <div className="flex flex-wrap gap-2">
            {can(PERMISSIONS.STUDENT_STATUS_UPDATE) && <StudentLifecycleMenu student={record} />}
            {can(PERMISSIONS.ENROLLMENT_CREATE) && (
              <Button variant="secondary" onClick={() => setEnrolling(true)}>
                Enrol
              </Button>
            )}
            {can(PERMISSIONS.STUDENT_UPDATE) && <Button onClick={() => setEditing(true)}>Edit</Button>}
            {can(PERMISSIONS.STUDENT_DELETE) && (
              <Button variant="ghost" onClick={() => setDeleting(true)}>
                Delete
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 flex gap-1 border-b border-line" role="tablist">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            role="tab"
            aria-selected={tab === entry.id}
            onClick={() => setTab(entry.id)}
            className={
              tab === entry.id
                ? 'border-b-2 border-accent px-3 py-2 text-sm font-medium text-ink'
                : 'border-b-2 border-transparent px-3 py-2 text-sm text-muted hover:text-ink'
            }
          >
            {entry.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <>
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Details" />
          <dl className="grid gap-x-8 gap-y-4 px-5 py-4 sm:grid-cols-2">
            <Detail label="Status" value={<StatusBadge status={record.status} />} />
            <Detail label="Date of birth" value={formatDate(record.dateOfBirth)} />
            <Detail label="Gender" value={humanise(record.gender)} />
            <Detail label="Admission date" value={formatDate(record.admissionDate)} />
            <Detail label="E-mail" value={orDash(record.email)} />
            <Detail label="Phone" value={orDash(record.phone)} />
            <Detail label="Blood group" value={orDash(record.bloodGroup)} />
            <Detail label="Nationality" value={orDash(record.nationality)} />
          </dl>
        </Card>

        <Card>
          <CardHeader title="Current placement" description="From the current academic year" />
          <div className="px-5 py-4">
            {placement === undefined ? (
              <p className="text-sm text-muted">
                This student has no active enrolment in the current academic year.
              </p>
            ) : (
              <dl className="space-y-4">
                <Detail label="Academic year" value={placement.academicYearName} />
                <Detail label="Grade" value={placement.gradeName} />
                <Detail label="Section" value={orDash(placement.sectionName)} />
                <Detail label="Roll number" value={orDash(placement.rollNumber)} />
              </dl>
            )}
          </div>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <StudentStatusHistoryPanel studentId={id} />
        </div>
        <PortalAccountPanel
          subject="this student"
          role="STUDENT"
          queryKey={queryKeys.students.account(id)}
          load={() => studentsApi.account(id)}
          link={(userId) => studentsApi.linkAccount(id, userId)}
          unlink={() => studentsApi.unlinkAccount(id)}
        />
      </div>
        </>
      )}

      {tab === 'guardians' && <StudentGuardiansPanel studentId={id} />}

      {tab === 'history' && <AcademicHistoryPanel studentId={id} />}

      {tab === 'records' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <StudentIdentificationsPanel studentId={id} />
          <StudentDocumentsPanel studentId={id} />
        </div>
      )}

      <StudentFormModal
        open={editing}
        student={record}
        saving={update.isPending}
        onClose={() => setEditing(false)}
        onSubmit={(values, setError) =>
          update.mutate(toPayload(values), { onError: (error) => handleApiError(error, setError) })
        }
      />

      <EnrollStudentModal
        open={enrolling}
        student={record}
        onClose={() => setEnrolling(false)}
        onEnrolled={() => {
          setEnrolling(false);
          void queryClient.invalidateQueries({ queryKey: queryKeys.students.detail(id) });
          void queryClient.invalidateQueries({ queryKey: queryKeys.studentProfile.all });
        }}
      />

      <ConfirmDialog
        open={deleting}
        title="Delete student"
        message={`Delete ${record.fullName}? This is refused once the student has any enrolment history — withdraw them instead.`}
        confirmLabel="Delete"
        destructive
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setDeleting(false)}
      />
    </>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-subtle">{label}</dt>
      <dd className="mt-1 text-sm text-ink">{value}</dd>
    </div>
  );
}
